/**
 * SectionHeader
 * @module components/SectionHeader
 */

import clsx from 'clsx';
import {
	GenericWarning,
	renderElementBasedOnEditMode,
	ServerError,
	t,
} from 'common/helpers';
import CustomExternalScript from 'components/framework/CustomExternalScript';
import RichText from 'components/ui/RichText';
import React from 'react';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { UpdateCart } from 'store/modules/cart';
import { FastCheckout } from 'types/cart-types';
import { ContentButton } from './SectionHeader.styles';
import {
	SectionHeaderLayout,
	SectionHeaderWrapper,
} from './SectionHeader.styles';
import { useSiteType } from 'context/siteType.context';

interface Sectionheader {
	title?: string;
	fastCheckout?: FastCheckout;
	heading: string;
	headingLevel?: string;
	text?: string;
	size: 'small' | 'large' | 'default';
	isInEditMode?: boolean;
	className?: string;
}

const SectionHeader = (props: Sectionheader) => {
	const {
		heading,
		text,
		fastCheckout,
		size,
		isInEditMode,
		headingLevel,
		className,
	} = props;
	const dispatch = useDispatch();
	const siteType = useSiteType();
	const handleFastCheckoutChange = async () => {
		try {
			dispatch(
				UpdateCart({
					selectedFastCheckout: fastCheckout?.active,
				})
			);
		} catch (err) {
			if (err instanceof ServerError) {
				toast(t('shared/toast/servererror'), { type: 'error' });
			} else if (err instanceof GenericWarning) {
				toast(err.message, { type: 'warning' });
			}
		}
	};

	return (
		<SectionHeaderLayout
			className={clsx(
				className,
				'mb-0',
				siteType === 'LindabGroup' && 'bg-lgLightGrayBlue'
			)}
		>
			<SectionHeaderWrapper as="section">
				<div className="flex w-full flex-col md:flex-row justify-between items-center">
					<div className="flex items-cente flex-col">
						{heading &&
							renderElementBasedOnEditMode(
								isInEditMode || false,
								'heading',
								'h1',
								{
									className: `font-standard mb-6 mt-6 font-normal text-h2 md:text-h1 ${
										siteType === 'LindabGroup' ? 'text-lgDarkBlue' : 'text-blue'
									}`,
									as: headingLevel,
								},
								heading
							)}

						<RichText
							name="text"
							text={text}
							size={size}
							isInEditMode={isInEditMode}
							className={clsx(siteType === 'LindabGroup' && 'text-lgDarkGray')}
						/>
					</div>
					{fastCheckout && (
						<div
							className="inline-block bg-greyDark p-4 md:max-w-3xl relative max-w-full"
							role="alert"
						>
							<RichText
								name="fastChekout text"
								text={fastCheckout?.text}
								isInEditMode={isInEditMode}
							/>
							<ContentButton
								buttonColor={fastCheckout?.active ? 'orange' : 'green'}
								onClick={() => handleFastCheckoutChange()}
								type="button"
							>
								{fastCheckout?.active ? (
									<div className="flex items-center justify-around gap-3">
										<FiChevronsLeft />
										<span className="truncate overflow-ellipsis max-w-75">
											{fastCheckout.buttonText}
										</span>
									</div>
								) : (
									<div className="flex items-center justify-around gap-3">
										<span className="truncate overflow-ellipsis max-w-75">
											{fastCheckout.buttonText}
										</span>
										<FiChevronsRight />
									</div>
								)}
							</ContentButton>
						</div>
					)}
				</div>
			</SectionHeaderWrapper>
			<CustomExternalScript />
		</SectionHeaderLayout>
	);
};

export default SectionHeader;
